<template>
  <v-container class="mt-5 mb-15 px-10" fluid>
    <PageHeader>
      <template #header>
        <img
          v-if="pluginLogo"
          :src="pluginLogo"
          :alt="`${pluginName} logo`"
          height="32"
        >
        {{ installed ? 'Update ' : 'Install' }} {{ pluginName }} Plugin
      </template>
      <template #subheader>
        Please configure the following in order to install the {{ pluginName }} plugin.
      </template>
    </PageHeader>

    <div class="section-header">
      Configuration
    </div>
    <div>
      <CustomTextInput
        v-model="nonPrescribingMonths"
        header="Number of Months Defining Non-Prescribing Behavior"
        type="number"
        hint="A contact is considered a &quot;non-prescriber&quot; if they have not prescribed within the number of months entered here."
      />
    </div>
    <div>
      <v-checkbox
        v-model="importCreatesNewContacts"
        label="Prescription Data Imports create new contacts if no match is found"
        hide-details
        class="mt-8"
      />
    </div>

    <validation-observer ref="autoImportConfigs">
      <AutomatedImportConfigurations
        :id="id"
        :plugin-data="pluginData"
        :import-configurations="importConfigurations"
        :import-type="importType"
        :file-extensions="fileExtensionOptions"
        @add-configuration="addImportConfiguration"
        @delete-configuration="deleteImportConfiguration"
        @update-configuration="updateImportValue"
      />
    </validation-observer>

    <v-row class="mt-12">
      <v-col cols="12" class="d-flex justify-end">
        <v-btn
          v-if="installed"
          class="custom-button custom-button--red-text px-8 mr-4"
          height="34px"
          depressed
          @click="deactivateDialog = true;"
        >
          Deactivate
        </v-btn>
        <v-btn
          class="custom-button custom-button--blue px-13"
          height="34px"
          depressed
          :block="$vuetify.breakpoint.xsOnly"
          @click="installPlugin(importConfigurations, false)"
        >
          {{ installed ? "Configure" : "Install" }}
        </v-btn>
      </v-col>
    </v-row>
    <DeactivateConfirmDialog
      v-if="deactivateDialog"
      :id="id"
      :plugin="pluginData"
      @dismiss="deactivateDialog = false;"
    />
  </v-container>
</template>

<script>
import importExportMixin from "@/utils/import-export-mixin.js";
import PageHeader from "@/sharedComponents/PageHeader";
import CustomTextInput from "@/sharedComponents/CustomTextInput";
import AutomatedImportConfigurations from "@/views/Plugins/components/AutomatedImportConfigurations";
import DeactivateConfirmDialog from "./components/DeactivateConfirmDialog";

export default {
  name: "AddPluginPrescriptionData",
  components: {
    AutomatedImportConfigurations,
    PageHeader,
    CustomTextInput,
    DeactivateConfirmDialog
  },
  mixins: [importExportMixin],
  props: {
    id: {
      default: () => null,
      type: [Number, String],
    },
    installed: {
      default: false,
      type: Boolean,
    },
  },
  data: () => ({
    deactivateDialog: false,
    pluginData: null,
    pluginLogo: null,
    pluginName: null,
    nonPrescribingMonths: 3,
    importCreatesNewContacts: true,
    importType: 'rx_data',
    fileExtensionOptions: [
      { id: 'csv', text: 'Comma-Separated Values (.csv)' },
      { id: 'txt', text: 'Text File (.txt)' },
    ],
  }),
  created() {
    if (this.installed) {
      this.loadAccountPlugin();
    } else {
      this.getPluginDataById();
    }
  },
  methods: {
    async getPluginDataById() {
      const resp = await this.$rest.plugins.getPluginDataById(this.id);
      if (!resp.data) {
        return;
      }
      this.pluginLogo = require(`@/assets/plugins/${resp?.data?.image}`);
      this.pluginName = resp?.data?.name;
    },
    async loadAccountPlugin() {
      const resp = await this.$rest.plugins.get_resource(this.id, {
        includeCredentials: true,
      });
      if (!resp.data) {
        return;
      }

      this.pluginData = resp.data;
      this.pluginLogo = require(`@/assets/plugins/${resp?.data?.plugin?.image}`);
      this.pluginName = resp?.data?.plugin?.name;

      this.nonPrescribingMonths = resp.data.pluginConfiguration?.nonPrescribingMonths ?? 3;
      this.importCreatesNewContacts = resp.data.pluginConfiguration?.importCreatesNewContacts ?? true;

      if (resp.data.pluginConfiguration?.importConfigurations.length) {
        const credentials = resp.data.pluginConfiguration?.credentials;

        this.setupImportConfigurations(resp.data.pluginConfiguration?.importConfigurations, credentials);
      }
    },
    async installPlugin(expectedImportConfig, returnResponse) {
      if (!await this.$refs.autoImportConfigs.validate()) {
        this.$store.commit('snackbar/showMessage', {
          content: 'Please fix the errors in all Import Configurations',
          color: 'error',
        });

        return;
      }

      const pluginConfiguration = this.getFormattedPluginConfiguration(expectedImportConfig);
      pluginConfiguration.nonPrescribingMonths = this.nonPrescribingMonths ? this.nonPrescribingMonths : null;
      pluginConfiguration.importCreatesNewContacts = this.importCreatesNewContacts;

      let response = null;
      if (this.installed) {
        response = await this.$rest.plugins
          .put_resource(this.id, {
            isActive: true,
            pluginConfiguration,
          })
          .catch(error => {
            let error_message = null;
            let errors_list = "";
            if (error.response?.data?.errors?.length) {
              error.response.data.errors.forEach(item => {
                errors_list += (item.error + "\r \n");
              });
              error_message = errors_list;
            } else if (error.response?.data?.message) {
              error_message = error.response?.data?.message;
            }

            this.$store.commit('snackbar/showMessage', {
              content: error_message || 'Failed when trying to configure plugin',
              color: 'error',
            });
          });
      } else {
        response = await this.$rest.plugins
          .post_resource({
            plugin: +this.id,
            isActive: true,
            pluginConfiguration,
          })
          .then((resp) => {
            const selected_account = this.$store.getters["user/account"];
            this.$store.commit("user/update_selected_account", {
              ...selected_account,
              activePlugins: [...selected_account.activePlugins, resp.data],
            });

            return resp;
          })
          .catch(() => {
            this.$store.commit('snackbar/showMessage', {
              content: 'Failed when trying to install plugin',
              color: 'error',
            });
          });
      }

      if (returnResponse) {
        return response;
      }
      if (!response?.data) {
        return;
      }

      this.$store.commit('snackbar/showMessage', {
        content: this.installed
          ? 'Plugin configured successfully'
          : 'Plugin installed successfully',
        color: 'success',
      });

      await this.$router.push({ name: 'Plugins' });
    }
  },
};
</script>

<style scoped>
.section-header {
  margin-top: 30px;
  font-size: 1.25rem;
  font-weight: bold;
}
</style>
